import React, {useId, useLayoutEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Button, Dialog, createSlotRecipeContext, VisuallyHidden} from '@chakra-ui/react3'
import {FormattedMessage, useIntl} from 'react-intl'
import Slider from '../../../../../leap/components/cms/slider'
import {ArrowShrinkIcon, CenterFocusIcon} from '../../../../../leap/components/icons'
import ZoomImage from '../../../../../leap/components/zoomImage/zoomImage'
import XpdpConfiguratorGalleryItem from '../gallery-item'
import {zoomOverlayRecipe} from './recipe'

const {StylesProvider, useRecipeResult, useStyles} = createSlotRecipeContext({
    recipe: zoomOverlayRecipe
})
const EMPTY_RECIPE_PROPS = {}
const OVERLAY_EXPAND_DURATION = 200
const getOverlayExpandDuration = () =>
    window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ? 0 : OVERLAY_EXPAND_DURATION

const XpdpGalleryZoomOverlayContent = ({
    items,
    initialIndex,
    currentIndex,
    onIndexChange,
    zoomImperativeRef
}) => {
    const {formatMessage} = useIntl()
    const styles = useStyles()
    const sliderId = useId()
    const sliderRef = useRef()

    return (
        <Slider
            ref={sliderRef}
            initialIndex={initialIndex}
            navigationButtonSize="sm"
            variant="configuratorGallery"
            id={sliderId}
            sliderLabel={formatMessage({
                id: 'configurator.gallery.zoom_overlay.slider_label',
                defaultMessage: 'Zoomed gallery'
            })}
            onIndexChange={onIndexChange}
        >
            {items.map((item, index) => (
                <ZoomImage
                    key={index}
                    imageName={item.product?.name}
                    css={styles.zoomFrame}
                    zoomImperativeRef={index === currentIndex ? zoomImperativeRef : undefined}
                >
                    <XpdpConfiguratorGalleryItem
                        product={item.product}
                        stack={item.stack}
                        isExpanded
                    />
                </ZoomImage>
            ))}
        </Slider>
    )
}

XpdpGalleryZoomOverlayContent.propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.shape({
            product: PropTypes.object,
            stack: PropTypes.object
        })
    ),
    initialIndex: PropTypes.number,
    currentIndex: PropTypes.number,
    onIndexChange: PropTypes.func,
    zoomImperativeRef: PropTypes.shape({current: PropTypes.any})
}

const XpdpGalleryZoomOverlay = ({
    open,
    onOpenChange,
    onExitComplete,
    items = [],
    initialIndex = 0
}) => {
    const {formatMessage} = useIntl()
    const {styles} = useRecipeResult(EMPTY_RECIPE_PROPS)
    const [currentIndex, setCurrentIndex] = useState(initialIndex)
    const [dialogOpen, setDialogOpen] = useState(open)
    const [content, setContent] = useState(null)
    const zoomImperativeRef = useRef(null)
    const galleryHeightRef = useRef(null)

    useLayoutEffect(() => {
        if (open) setDialogOpen(true)
    }, [open])

    // opening animation
    useLayoutEffect(() => {
        if (!open || !content) {
            return
        }

        const animationFrame = window.requestAnimationFrame(() => {
            const {height} = content.getBoundingClientRect()
            if (!height) return

            galleryHeightRef.current ??= height
            content.animate([{height: `${height}px`}, {height: '100dvh'}], {
                duration: getOverlayExpandDuration(),
                easing: 'linear',
                fill: 'forwards'
            })
        })

        return () => window.cancelAnimationFrame(animationFrame)
    }, [content, open])

    // closing animation
    useLayoutEffect(() => {
        if (open || !content) return
        // open = false overlay closing

        const animation = content.animate(
            [{height: '100dvh'}, {height: `${galleryHeightRef.current}px`}],
            {
                duration: getOverlayExpandDuration(),
                easing: 'linear',
                fill: 'forwards'
            }
        )

        // after finish the animation then run exit callback
        animation.finished.then(() => onExitComplete?.()).catch(() => {})
    }, [content, onExitComplete, open])

    return (
        <StylesProvider value={styles}>
            <Dialog.Root
                placement="top"
                size="full"
                scrollBehavior="outside"
                open={dialogOpen}
                onOpenChange={onOpenChange}
            >
                <Dialog.Backdrop>
                    <Dialog.Positioner>
                        <Dialog.Content ref={setContent} css={styles.content}>
                            <Dialog.Title>
                                <VisuallyHidden>
                                    <FormattedMessage
                                        id="configurator.gallery.zoom_overlay.title"
                                        defaultMessage="Zoomed product gallery"
                                    />
                                </VisuallyHidden>
                            </Dialog.Title>
                            <Dialog.Body css={styles.body}>
                                <XpdpGalleryZoomOverlayContent
                                    items={items}
                                    initialIndex={initialIndex}
                                    currentIndex={currentIndex}
                                    onIndexChange={setCurrentIndex}
                                    zoomImperativeRef={zoomImperativeRef}
                                />
                            </Dialog.Body>
                            <Dialog.Footer css={styles.actions}>
                                <Button
                                    variant="mediaFilled"
                                    size="md"
                                    aria-label={formatMessage({
                                        id: 'configurator.gallery.button.center_focus',
                                        defaultMessage: 'Center gallery item'
                                    })}
                                    onClick={() => zoomImperativeRef.current?.reset?.()}
                                >
                                    <CenterFocusIcon aria-hidden="true" />
                                </Button>
                                <Button
                                    variant="mediaFilled"
                                    size="md"
                                    aria-label={formatMessage({
                                        id: 'configurator.gallery.button.shrink',
                                        defaultMessage: 'Shrink gallery item'
                                    })}
                                    onClick={() => onOpenChange?.({open: false})}
                                >
                                    <ArrowShrinkIcon aria-hidden="true" />
                                </Button>
                            </Dialog.Footer>
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Dialog.Backdrop>
            </Dialog.Root>
        </StylesProvider>
    )
}

XpdpGalleryZoomOverlay.propTypes = {
    open: PropTypes.bool,
    onOpenChange: PropTypes.func,
    onExitComplete: PropTypes.func,
    items: PropTypes.arrayOf(
        PropTypes.shape({
            product: PropTypes.object,
            stack: PropTypes.object
        })
    ),
    initialIndex: PropTypes.number
}

export default XpdpGalleryZoomOverlay
